import { useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import {
  RefreshCw,
  Globe,
  Database,
  MessageCircle,
  Lock,
  Activity,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Zap,
  TrendingUp,
  WifiOff,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { TenantHealthStatus } from "@platform/shared";

// ---------------------------------------------------------------------------
// Types & config
// ---------------------------------------------------------------------------

type OverallStatus = "healthy" | "degraded" | "down";

interface StatusMeta {
  readonly label: string;
  readonly badgeVariant: "success" | "warning" | "danger";
  readonly borderClass: string;
  readonly glowClass: string;
  readonly icon: ReactNode;
  readonly pulse: boolean;
}

const STATUS_META: Record<OverallStatus, StatusMeta> = {
  healthy: {
    label: "תקין",
    badgeVariant: "success",
    borderClass: "border-green-200/70 dark:border-green-800/50",
    glowClass: "shadow-green-100/60 dark:shadow-green-900/20",
    icon: <CheckCircle2 className="h-5 w-5 text-green-500" />,
    pulse: false,
  },
  degraded: {
    label: "חלקי",
    badgeVariant: "warning",
    borderClass: "border-yellow-300/80 dark:border-yellow-700/50",
    glowClass: "shadow-yellow-100/80 dark:shadow-yellow-900/20",
    icon: <AlertTriangle className="h-5 w-5 text-yellow-500" />,
    pulse: true,
  },
  down: {
    label: "מושבת",
    badgeVariant: "danger",
    borderClass: "border-red-300/80 dark:border-red-700/50",
    glowClass: "shadow-red-100/80 dark:shadow-red-900/20",
    icon: <XCircle className="h-5 w-5 text-red-500" />,
    pulse: true,
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "עכשיו";
  if (mins < 60) return `לפני ${String(mins)} דק'`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `לפני ${String(hours)} שע'`;
  return `לפני ${String(Math.floor(hours / 24))} ימים`;
}

// ---------------------------------------------------------------------------
// Check row inside a service card
// ---------------------------------------------------------------------------

interface CheckRowProps {
  readonly ok: boolean | null;
  readonly label: string;
  readonly icon: ReactNode;
  readonly nullable?: boolean;
}

function CheckRow({ ok, label, icon, nullable = false }: CheckRowProps): ReactNode {
  if (nullable && ok === null) {
    return (
      <div className="flex items-center gap-2 rounded-md bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground">
        {icon}
        <span className="flex-1">{label}</span>
        <span className="font-mono text-[10px]">N/A</span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium",
        ok
          ? "bg-green-50 text-green-800 dark:bg-green-950/20 dark:text-green-400"
          : "bg-red-50 text-red-800 dark:bg-red-950/20 dark:text-red-400",
      )}
    >
      {icon}
      <span className="flex-1">{label}</span>
      {ok ? (
        <CheckCircle2 className="h-3 w-3 text-green-500" />
      ) : (
        <XCircle className="h-3 w-3 text-red-500" />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Service health card — uses TenantHealthStatus directly from API
// ---------------------------------------------------------------------------

function HealthCard({
  health,
}: {
  readonly health: TenantHealthStatus;
}): ReactNode {
  const meta = STATUS_META[health.overall];

  // Derive a display name: use tenant_name if present (joined fields), else tenantId
  const displayName =
    "tenant_name" in health && typeof (health as Record<string, unknown>).tenant_name === "string"
      ? (health as Record<string, unknown>).tenant_name as string
      : health.tenantId;

  return (
    <Card
      className={cn(
        "relative overflow-hidden transition-all duration-200",
        "hover:shadow-lg hover:-translate-y-0.5",
        meta.borderClass,
        "shadow-md",
        meta.glowClass,
      )}
    >
      {/* Top status strip */}
      <div
        className={cn(
          "absolute inset-x-0 top-0 h-1",
          health.overall === "healthy" && "bg-gradient-to-r from-green-400 to-emerald-500",
          health.overall === "degraded" && [
            "bg-gradient-to-r from-yellow-400 to-orange-400",
            meta.pulse && "animate-pulse",
          ],
          health.overall === "down" && [
            "bg-gradient-to-r from-red-400 to-rose-500",
            meta.pulse && "animate-pulse",
          ],
        )}
      />

      <CardContent className="p-5 pt-6 space-y-4">
        {/* Header: name + status badge */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <span className={meta.pulse ? "animate-pulse" : undefined}>
              {meta.icon}
            </span>
            <h3 className="font-bold text-foreground leading-tight text-sm">
              {displayName}
            </h3>
          </div>
          <Badge variant={meta.badgeVariant} className="shrink-0 text-[11px]">
            {meta.label}
          </Badge>
        </div>

        {/* Checks grid */}
        <div className="grid grid-cols-2 gap-1.5">
          <CheckRow
            ok={health.checks.siteUp}
            label="Site"
            icon={<Globe className="h-3.5 w-3.5 shrink-0" />}
          />
          <CheckRow
            ok={health.checks.d1Responsive}
            label="DB"
            icon={<Database className="h-3.5 w-3.5 shrink-0" />}
          />
          <CheckRow
            ok={health.checks.waBridge}
            label="WA Bridge"
            icon={<MessageCircle className="h-3.5 w-3.5 shrink-0" />}
            nullable
          />
          <CheckRow
            ok={health.checks.sslValid}
            label="SSL"
            icon={<Lock className="h-3.5 w-3.5 shrink-0" />}
          />
        </div>

        {/* Footer: last checked */}
        <div className="flex items-center gap-1.5 border-t pt-3 text-[11px] text-muted-foreground">
          <Clock className="h-3 w-3 shrink-0" />
          <span>בדיקה אחרונה: {timeAgo(health.checkedAt)}</span>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Summary bar with progress strip
// ---------------------------------------------------------------------------

function SummaryBar({
  statuses,
}: {
  readonly statuses: ReadonlyArray<TenantHealthStatus>;
}): ReactNode {
  const total = statuses.length;
  if (total === 0) return null;

  const counts = statuses.reduce(
    (acc, s) => {
      acc[s.overall]++;
      return acc;
    },
    { healthy: 0, degraded: 0, down: 0 } as Record<OverallStatus, number>,
  );

  const healthyPct = Math.round((counts.healthy / total) * 100);
  const degradedPct = Math.round((counts.degraded / total) * 100);
  const downPct = 100 - healthyPct - degradedPct;

  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm space-y-4">
      {/* Top row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">מצב מערכת</p>
          <p className="text-2xl font-bold text-foreground">
            {counts.healthy}/{total}
            <span className="ms-2 text-sm font-medium text-muted-foreground">שירותים תקינים</span>
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 dark:bg-green-950/30">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <span className="text-sm font-bold text-green-700 dark:text-green-400 tabular-nums">{counts.healthy}</span>
            <span className="text-xs text-green-600 dark:text-green-500">תקינים</span>
          </div>
          {counts.degraded > 0 && (
            <div className="flex items-center gap-2 rounded-lg bg-yellow-50 px-3 py-2 dark:bg-yellow-950/30">
              <AlertTriangle className="h-4 w-4 text-yellow-500 animate-pulse" />
              <span className="text-sm font-bold text-yellow-700 dark:text-yellow-400 tabular-nums">{counts.degraded}</span>
              <span className="text-xs text-yellow-600 dark:text-yellow-500">חלקיים</span>
            </div>
          )}
          {counts.down > 0 && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 dark:bg-red-950/30">
              <WifiOff className="h-4 w-4 text-red-500 animate-pulse" />
              <span className="text-sm font-bold text-red-700 dark:text-red-400 tabular-nums">{counts.down}</span>
              <span className="text-xs text-red-600 dark:text-red-500">מושבתים</span>
            </div>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="space-y-1.5">
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-green-500 transition-all duration-700"
            style={{ width: `${String(healthyPct)}%` }}
          />
          <div
            className="h-full bg-yellow-400 transition-all duration-700"
            style={{ width: `${String(degradedPct)}%` }}
          />
          {downPct > 0 && (
            <div
              className="h-full bg-red-500 transition-all duration-700"
              style={{ width: `${String(downPct)}%` }}
            />
          )}
        </div>
        <p className="text-[11px] text-muted-foreground">
          {healthyPct}% זמינות כוללת
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SuperHealthPage(): ReactNode {
  const [statuses, setStatuses] = useState<ReadonlyArray<TenantHealthStatus>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatuses = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    fetch("/super/api/health", { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ success: boolean; data: TenantHealthStatus[] }>;
      })
      .then((json) => {
        if (!json.success) throw new Error("API error");
        setStatuses(json.data);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  }, []);

  useEffect(() => { fetchStatuses(); }, [fetchStatuses]);

  // Auto-refresh every 30s
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => { fetchStatuses(true); }, 30000);
    } else {
      if (autoRefreshRef.current !== null) {
        clearInterval(autoRefreshRef.current);
        autoRefreshRef.current = null;
      }
    }
    return () => {
      if (autoRefreshRef.current !== null) {
        clearInterval(autoRefreshRef.current);
        autoRefreshRef.current = null;
      }
    };
  }, [autoRefresh, fetchStatuses]);

  const handleCheckNow = useCallback(() => {
    setIsRefreshing(true);
    fetch("/super/api/health/check", {
      method: "POST",
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ success: boolean }>;
      })
      .then(() => { fetchStatuses(true); })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Check failed");
      })
      .finally(() => setIsRefreshing(false));
  }, [fetchStatuses]);

  return (
    <div className="space-y-6 p-4 md:p-6" dir="rtl">
      {/* Page header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Activity className="h-6 w-6 text-primary" />
            מוניטורינג בריאות
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {loading ? "טוען..." : `${String(statuses.length)} דיירים בניטור`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Auto-refresh toggle */}
          <button
            onClick={() => setAutoRefresh((v) => !v)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all min-h-[44px] cursor-pointer",
              autoRefresh
                ? "bg-primary text-primary-foreground"
                : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
            title={autoRefresh ? "כבה רענון אוטומטי" : "הפעל רענון כל 30 שניות"}
          >
            <Zap className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">
              {autoRefresh ? "רענון אוטומטי פעיל" : "רענון אוטומטי"}
            </span>
          </button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleCheckNow}
            disabled={isRefreshing}
            className="min-h-[44px] gap-2"
          >
            <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
            <span className="hidden sm:inline">בדוק עכשיו</span>
          </Button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="py-12 text-center text-muted-foreground text-sm">טוען...</div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="py-12 text-center text-destructive text-sm">{error}</div>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {/* Summary */}
          <SummaryBar statuses={statuses} />

          {/* Service cards grid */}
          {statuses.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground text-sm">
              אין נתוני בריאות — לחץ &quot;בדוק עכשיו&quot; להפעלת בדיקה
            </div>
          ) : (
            <div>
              <h2 className="mb-3 text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                סטטוס לפי דייר
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {statuses.map((s) => (
                  <HealthCard key={s.tenantId} health={s} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
